import {
	isDocsApiReferencePath,
	isDocsHelpCenterPath,
	isDocsKnowledgeBasePath,
} from "@/lib/docs-paths";
import {
	isDocsSurfaceEnabled,
	isKnowledgeBaseSurfaceEnabled,
	isMcpSurfaceEnabled,
	isModuleEnabled,
	isOperationsLegalEnabled,
	isOperationsSecurityEnabled,
} from "@/lib/modules";

type MarketingModule =
	| "billing"
	| "marketing_blog"
	| "marketing_changelog"
	| "mobile_app"
	| "public_api"
	| "docs"
	| "knowledge_base"
	| null;

export type MarketingNavLink = {
	name: string;
	href: string;
	description?: string;
	module?: MarketingModule;
	/** Show when public_api.mcp is enabled (not a top-level module id). */
	requiresMcp?: boolean;
	/** Show when operations legal or security document hubs are enabled. */
	operationsLegal?: boolean;
	isActive?: (pathname: string) => boolean;
	/** When false, matching this link does not highlight the parent nav group. */
	contributesToGroupActive?: boolean;
};

export type MarketingNavFeaturedIcon = "mcp";

export type MarketingNavFeatured = {
	name: string;
	description: string;
	href: string;
	badge?: string;
	pattern?: "grid" | "orbit" | "stack" | "shield";
	icon?: MarketingNavFeaturedIcon;
	module?: MarketingModule;
	requiresMcp?: boolean;
	operationsLegal?: boolean;
	isActive?: (pathname: string) => boolean;
	/** When false, matching this link does not highlight the parent nav group. */
	contributesToGroupActive?: boolean;
};

export type MarketingNavItem = {
	name: string;
	href?: string;
	description?: string;
	module?: MarketingModule;
	items?: MarketingNavLink[];
	featured?: MarketingNavFeatured[];
	menuLayout?: "mega" | "list" | "grid";
	linkColumns?: number;
};

type MarketingNavCandidate = MarketingNavItem;

const marketingNavCandidates: MarketingNavCandidate[] = [
	{
		name: "Product",
		menuLayout: "mega",
		linkColumns: 1,
		items: [
			{
				name: "What's included",
				href: "/features",
			},
			{
				name: "Mobile app",
				href: "/download",
				module: "mobile_app",
			},
		],
	},
	{
		name: "Docs",
		menuLayout: "mega",
		linkColumns: 1,
		module: "docs",
		items: [
			{
				name: "Documentation",
				href: "/docs",
				module: "docs",
				isActive: isDocsHelpCenterPath,
			},
			{
				name: "API Reference",
				href: "/docs/api-reference",
				module: "public_api",
				isActive: isDocsApiReferencePath,
			},
			{
				name: "Knowledge Base",
				href: "/docs/knowledge-base",
				module: "knowledge_base",
				isActive: isDocsKnowledgeBasePath,
			},
		],
		featured: [
			{
				name: "MCP integration",
				description: "Connect Cursor, Claude Desktop, or other MCP clients",
				href: "/docs/api-reference/guides/mcp-integration",
				pattern: "orbit",
				icon: "mcp",
				requiresMcp: true,
			},
		],
	},
	{
		name: "Resources",
		menuLayout: "mega",
		linkColumns: 1,
		items: [
			{ name: "Blog", href: "/blog", module: "marketing_blog" },
			{
				name: "Change Log",
				href: "/changelog",
				module: "marketing_changelog",
			},
			{ name: "Transparency", href: "/transparency" },
			{
				name: "Legal & security",
				href: "/legal",
				operationsLegal: true,
			},
			{ name: "Contact", href: "/contact" },
		],
	},
	{
		name: "Pricing",
		href: "/pricing",
		module: "billing",
	},
];

function isOperationsNavEnabled(): boolean {
	return isOperationsLegalEnabled() || isOperationsSecurityEnabled();
}

function isLinkEnabled(link: {
	module?: MarketingModule;
	requiresMcp?: boolean;
	operationsLegal?: boolean;
}): boolean {
	if (link.operationsLegal && !isOperationsNavEnabled()) {
		return false;
	}
	if (link.requiresMcp && !isMcpSurfaceEnabled()) {
		return false;
	}
	if (link.module === "public_api") {
		return isModuleEnabled("public_api");
	}
	if (link.module === "docs") {
		return isDocsSurfaceEnabled();
	}
	if (link.module === "knowledge_base") {
		return isKnowledgeBaseSurfaceEnabled();
	}
	return !link.module || isModuleEnabled(link.module);
}

function isDocsNavEnabled(): boolean {
	return (
		isDocsSurfaceEnabled() ||
		isKnowledgeBaseSurfaceEnabled() ||
		isModuleEnabled("public_api")
	);
}

function dedupeFeaturedAgainstItems(
	items: MarketingNavLink[],
	featured: MarketingNavFeatured[],
): MarketingNavFeatured[] {
	const itemHrefs = new Set(items.map((link) => link.href));
	return featured.filter((card) => !itemHrefs.has(card.href));
}

export function resolveMarketingNavItem(
	item: MarketingNavCandidate,
): MarketingNavItem | null {
	if (item.name === "Docs" && !isDocsNavEnabled()) {
		return null;
	}

	if (item.module && !isModuleEnabled(item.module)) {
		return null;
	}

	if (item.items?.length) {
		const items = item.items.filter(isLinkEnabled);
		const featured = dedupeFeaturedAgainstItems(
			items,
			item.featured?.filter(isLinkEnabled) ?? [],
		);

		if (items.length === 0 && featured.length === 0) {
			return null;
		}

		return {
			name: item.name,
			items,
			featured,
			menuLayout: item.menuLayout ?? "mega",
			linkColumns: item.linkColumns ?? 1,
		};
	}

	if (!item.href) {
		return null;
	}

	return {
		name: item.name,
		href: item.href,
		description: item.description,
	};
}

export const MarketingNavigationItems = marketingNavCandidates
	.map(resolveMarketingNavItem)
	.filter((item): item is MarketingNavItem => item !== null);

/** Flat leaf links — useful for tests and simple consumers. */
export const MarketingNavigation = MarketingNavigationItems.flatMap((item) => {
	const links = [
		...(item.items ?? []).map((link) => ({
			name: link.name,
			href: link.href,
		})),
		...(item.featured ?? []).map((card) => ({
			name: card.name,
			href: card.href,
		})),
	];

	if (links.length > 0) {
		return links;
	}

	return item.href ? [{ name: item.name, href: item.href }] : [];
}).filter(
	(link, index, all) =>
		all.findIndex((entry) => entry.href === link.href) === index,
);

export function flattenMarketingNavHrefs(items: MarketingNavItem[]): string[] {
	return items.flatMap((item) => {
		const hrefs = [
			...(item.items?.map((link) => link.href) ?? []),
			...(item.featured?.map((card) => card.href) ?? []),
		];

		if (hrefs.length > 0) {
			return hrefs;
		}

		return item.href ? [item.href] : [];
	});
}

export function splitNavLinksIntoColumns<T>(
	items: T[],
	columnCount: number,
): T[][] {
	if (items.length === 0 || columnCount <= 1) {
		return [items];
	}

	const columns: T[][] = Array.from({ length: columnCount }, () => []);

	for (const [index, item] of items.entries()) {
		const columnIndex = Math.floor((index * columnCount) / items.length);
		const column = columns[columnIndex];
		if (column) {
			column.push(item);
		}
	}

	return columns.filter((column) => column.length > 0);
}
